import * as React from 'react';
import { Component, Fragment } from 'react';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import { withTheme } from 'styled-components';
import { styled } from '../utils/styled';
import { propStyles } from '../utils/propStyles';
import SwipeableViews from 'react-swipeable-views';
import _FundFacts from './FundFacts';
import FundCommentary from './FundCommentary';
import ChartComponent from './Chart';
import KeyDocuments from './KeyDocuments';
import Disclaimers from './Disclaimers';
import withColumns from './withColumns';
import Tabs, { TabsProps, TabData } from './Tabs';
import {
    ChartStore,
    DataStore,
    EntityStore,
    RatingImageStore,
} from '../stores';
import { camelCase } from '@kurtosys/ksys-app-template/dist/utils/changeCase';
import TabRegion from '../models/components/TabRegion';
import getTabPanelProps from '../utils/getTabPanelProps';
import { Chart } from '../models/configuration/chart/Chart';
import { FundManagers as FundManagersConfig } from '../models/configuration/components/FundManagers';
import { FundManager } from '../models/entity';
import { FundManagersOrchestrationConfig } from '../models/configuration/components/FundManagersOrchestration';
import { FundCommentary as FundCommentaryConfig } from '../models/configuration/components/FundCommentary';
import { FundFacts } from '../models/configuration/components/FundFacts';
import { KeyDocProps } from '../models/configuration/components/KeyDocuments';

const FundFacts = withColumns(_FundFacts);

const FundCardMain = styled<any, any>('div', 'fundCardMain', 'fundCardTab')`
    ${propStyles};
`;

FundCardMain.defaultProps = {
    flex: '0 1 100%',
    minWidth: '0',
    paddingX: ['sm', 'md', 'lg'],
    paddingY: ['sm', 'md', 'lg'],
};

const SwipeyTabs = styled<any, any>(Tabs, 'swipeyTabs', 'fundCardTab')`
    position: relative;
    flex-wrap: nowrap;
    overflow-x: auto;
    scroll-behavior: smooth;
    scroll-snap-type: mandatory;
    scroll-snap-destination: center;
    &::-webkit-scrollbar {
        display: none;
    }
`;

SwipeyTabs.defaultProps = {
    zIndex: 'unset',
};

// Fixes issue with use of offsetHeight in react-swipeable-views
// Adding a wrapper with a border ensures offsetHeight value includes margin
const SwipeableViewWrapper = styled<any, any>(
    'div',
    'swipeableViewWrapper',
    'fundCardTab'
)`
    border: 1px solid transparent;
`;

// Swipeable view style overrides for expanded view
const expandedRootStyle = {
    overflow: 'visible',
};

const expandedContainerStyle = {
    flexDirection: 'column',
    height: 'auto',
};

const expandedSlideStyle = {
    overflow: 'visible',
};

type TabDataWithConfig = TabData & { config?: any };

interface FundCardTabProps {
    theme?: { [key: string]: React.CSSProperties };
    dataStore?: DataStore;
    entityStore?: EntityStore;
    ratingImageComponent: React.ReactElement;
    keyDocuments: Array<KeyDocProps>;
    charts: Array<Chart>;
    fundManagers: Array<FundManager>;
    fundManagersConfig: FundManagersConfig;
    fundManagersOrchestrationConfig: FundManagersOrchestrationConfig;
    fundManagersComponent: React.ReactElement;
    fundCommentary: FundCommentaryConfig;
    hasFundCommentary: boolean;
    fundFacts: FundFacts;
    ratingImageStore: RatingImageStore;
}

@inject('dataStore', 'entityStore')
@observer
class FundCardTab extends Component<FundCardTabProps, any> {
    @computed
    get tabsWithConfig(): Array<TabDataWithConfig> {
        const {
            dataStore,
            entityStore,
            hasFundCommentary,
            fundCommentary,
            fundFacts,
            fundManagers,
            fundManagersConfig,
            fundManagersOrchestrationConfig,
            ratingImageStore,
            keyDocuments,
        } = this.props;
        const tabs: Array<TabDataWithConfig> = [];

        if (hasFundCommentary) {
            this.addTab(tabs, {
                id: 'fundCommentary',
                label: fundCommentary.label || 'Commentary',
            });
        }

        if (Object.keys(fundFacts).length > 0) {
            this.addTab(tabs, {
                id: 'fundFacts',
                label: fundFacts.label,
            });
        }

        if (entityStore?.chartStores?.size > 0) {
            // Iterate through charts to check if chartHasData
            entityStore.chartStores.forEach(chartStore => {
                if (chartStore.displayChart) {
                    this.addTab(tabs, {
                        id: 'chart',
                        label: chartStore.chartConfig.label,
                        config: chartStore.chartConfig,
                    });
                }
            });
        }

        if (fundManagers.length > 0) {
            const config = fundManagersConfig;
            const tabManagersHeading =
                fundManagersOrchestrationConfig &&
                fundManagersOrchestrationConfig.mobileTabHeading;
            this.addTab(tabs, {
                id: 'fundManagers',
                label: tabManagersHeading || config.label,
            });
        }

        if (ratingImageStore.hasRatingImageOptions) {
            this.addTab(tabs, {
                id: 'ratingImage',
                label: ratingImageStore.headingText || 'Rating',
            });
        }

        if (keyDocuments.length > 0) {
            this.addTab(tabs, {
                id: 'keyDocuments',
                label: dataStore.config.rawRowConfig.keyDocuments.label,
            });
        }

        return tabs;
    }

    @computed
    get tabs(): Array<TabData> {
        return this.tabsWithConfig.map((tab, index) => ({
            id: tab.id,
            label: tab.label,
        }));
    }

    @computed
    get tabsProps(): TabsProps {
        const { theme, dataStore, entityStore } = this.props;
        return {
            region: TabRegion.MobileFundCard,
            tabs: this.tabs,
            setTabActive: (tab, e) => {
                const entityInfo: any = {};
                if (entityStore?.value) {
                    entityInfo.relatedEntity = dataStore.getEntityProperty(
                        entityStore.value as any,
                        dataStore.config.identifierProperty
                    );
                }
                dataStore.logDataLayer('tab_navigation', {
                    tabRegion: TabRegion.MobileFundCard,
                    tabLabel: tab.label,
                    ...entityInfo,
                });
                this.setSelectedTab(e, tab.id);
            },
            isTabSelected: tabId => tabId === this.selectedTab,
            getMetricContext: tab =>
                `fundCard-mobile-tab-${camelCase(tab.label)}`,
            tabThemeOverride: { flex: '0 0 auto', ...theme.FundCardMobileTab },
        };
    }

    @computed
    get selectedTabIndex(): number {
        const index = this.tabsWithConfig.findIndex(
            tab => tab.id === this.selectedTab
        );
        return index >= 0 ? index : 0;
    }
    @observable selectedTab: string;

    constructor(props: any) {
        super(props);
        if (this.tabsWithConfig) {
            const tab = this.tabs[0];
            if (tab) {
                this.selectedTab = tab.id;
            }
        }
    }

    addTab<T extends TabData>(tabs: Array<T>, tabData: T): void {
        tabData.id = `${tabData.id}-${tabs.length}`;
        tabs.push(tabData);
    }

    @action
    setSelectedTab = (tabNode, tabId: string) => {
        if (tabNode) {
            const tabLeft = tabNode.offsetLeft;
            const tabWidth = tabNode.offsetWidth;
            if (tabNode.parentNode) {
                const parentWidth = tabNode.parentNode.offsetWidth;
                tabNode.parentNode.scrollLeft =
                    tabLeft - (parentWidth - tabWidth) / 2;
            }
        }

        this.selectedTab = tabId;
    };

    @action
    setSelectedTabWithIndex = (index: number) => {
        const tab = this.tabsWithConfig[index];
        if (tab && tab.id) {
            this.setSelectedTab(null, tab.id);
        }
    };

    @action
    getFundCardComponent = (tabId, config) => {
        const {
            fundCommentary,
            fundFacts,
            fundManagersComponent,
            ratingImageStore,
            ratingImageComponent,
            keyDocuments,
        } = this.props;
        const tabParts = tabId.split('-');
        const tabType = tabParts[0];

        const { dataStore, entityStore } = this.props;
        if (tabType === 'fundCommentary') {
            return (
                <FundCommentary
                    showHeading={dataStore.isExpandAll}
                    fundCommentary={fundCommentary}
                />
            );
        }

        if (tabType === 'fundFacts') {
            return (
                <FundFacts
                    showHeading={dataStore.isExpandAll}
                    fundFacts={fundFacts}
                />
            );
        }

        if (
            ratingImageStore.ratingImagePosition === 'aboveFundManager' &&
            tabType === 'ratingImage'
        ) {
            return ratingImageComponent;
        }

        if (tabType === 'fundManagers') {
            return fundManagersComponent;
        }

        if (
            ratingImageStore.ratingImagePosition === 'belowFundManager' &&
            tabType === 'ratingImage'
        ) {
            return ratingImageComponent;
        }

        if (tabType === 'keyDocuments') {
            return (
                <KeyDocuments
                    showHeading={dataStore.isExpandAll}
                    keyDocuments={keyDocuments}
                />
            );
        }

        if (tabType === 'chart') {
            // TODO: work out how these stores are used and how we can use what was setup in dataStore.chartStores
            const chartStore = new ChartStore(
                dataStore.stores,
                config,
                entityStore.queryContext,
                entityStore.value
            );
            return (
                <ChartComponent
                    showHeading={dataStore.isExpandAll}
                    chartStore={chartStore}
                />
            );
        }

        return null;
    };

    render() {
        const {
            tabsWithConfig,
            selectedTabIndex,
            setSelectedTabWithIndex,
        } = this;
        const { theme, dataStore, entityStore } = this.props;
        return (
            <Fragment>
                {!dataStore.isExpandAll && (
                    <SwipeyTabs
                        {...this.tabsProps}
                        {...theme.FundCardMobileTabs}
                    />
                )}
                <FundCardMain {...theme.FundCardMobileMain}>
                    <SwipeableViews
                        disabled={dataStore.isExpandAll}
                        style={dataStore.isExpandAll ? expandedRootStyle : {}}
                        containerStyle={
                            dataStore.isExpandAll ? expandedContainerStyle : {}
                        }
                        slideStyle={
                            dataStore.isExpandAll ? expandedSlideStyle : {}
                        }
                        enableMouseEvents={true}
                        animateHeight={dataStore.isExpandAll ? false : true}
                        resistance={true}
                        index={selectedTabIndex}
                        onChangeIndex={index => setSelectedTabWithIndex(index)}
                    >
                        {tabsWithConfig.map((tab, index) => (
                            <SwipeableViewWrapper
                                key={tab.id}
                                {...getTabPanelProps(
                                    tab.id,
                                    TabRegion.MobileFundCard
                                )}
                            >
                                {this.getFundCardComponent(tab.id, tab.config)}
                            </SwipeableViewWrapper>
                        ))}
                    </SwipeableViews>
                </FundCardMain>
                <Disclaimers
                    disclaimers={entityStore.fundCardDisclaimers}
                    marginX="sm"
                    theme={{
                        ...theme,
                        DisclaimersList: theme.FundCardDisclaimersList,
                        DisclaimerListItem: theme.FundCardDisclaimerListItem,
                        DisclaimerCharacter: theme.FundCardDisclaimerCharacter,
                        DisclaimerContent: theme.FundCardDisclaimerContent,
                        DisclaimerReturnLink:
                            theme.FundCardDisclaimerReturnLink,
                    }}
                />
            </Fragment>
        );
    }
}

export default withTheme(FundCardTab) as any;



// WEBPACK FOOTER //
// ./src/Components/FundCardTab.tsx